import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'

import { HelpCircle, QrCode } from 'lucide-react'

import { useAuthStore } from '@/stores/auth'
import { useAppSettings } from '@/stores/useAppSettings'

import { paths } from '@/config/paths'

import { LanguageSwitcher } from '@/layouts/components/LanguageSwitcher'
import UserDropdown from '@/layouts/components/UserDropdown'

import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { SmartButton } from '@/components/ui-custom/SmartButton'

import { MemberQRCodeDialog } from '../../components/shared/MemberQRCodeDialog'
import { getMembershipStatus } from '../../features/membership/utils/membership'
import { AnnouncementBar } from './components/AnnouncementBar'
import { AppBreadcrumb } from './components/AppBreadcrumb'

export function SiteHeader() {
  const { t } = useTranslation(['dashboard'])
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { getSetting } = useAppSettings()
  const { message, showAlert, showSelectPlan, isExpired, isCancelled, isPaused } = getMembershipStatus()

  const isGymClosed = getSetting('operations.gym_closed', false)

  // Determine alert type
  const alertType = isExpired || isCancelled ? 'error' : isPaused ? 'info' : 'warning'

  return (
    <>
      {/* <NavigationBar /> */}
      {isGymClosed && (
        <AnnouncementBar
          type='error'
          title={t('dashboard:header.gymClosed.title', 'Gym Closed')}
          message={t(
            'dashboard:header.gymClosed.message',
            'The gym is temporarily closed. Access to facilities and services is currently unavailable.'
          )}
          dismissible={false}
          showIcon={true}
        />
      )}
      {showAlert && !isGymClosed && (
        <AnnouncementBar
          type={alertType}
          message={message}
          action={
            showSelectPlan
              ? {
                  label: t('dashboard:header.actions.selectPlan', 'Select Plan'),
                  onClick: () => navigate(paths.dashboard.member.selectPlan.getHref()),
                }
              : undefined
          }
        />
      )}
      {/* <NavigationProgress /> */}
      <header className='flex h-[var(--header-height)] shrink-0 items-center gap-2 border-b px-6'>
        <div className='flex flex-1 items-center gap-2'>
          <SidebarTrigger />
          <Separator orientation='vertical' className='mx-2 data-[orientation=vertical]:h-4' />
          {/* Breadcrumb navigation */}
          <AppBreadcrumb />
        </div>

        <div className='flex items-center gap-2'>
          <TooltipProvider>
            {/* Help Icon */}
            <Tooltip>
              <TooltipTrigger asChild>
                <SmartButton
                  size='icon'
                  variant='ghost'
                  className='text-muted-foreground relative size-8 rounded-sm shadow-none'
                  aria-label={t('dashboard:header.help.ariaLabel', 'Need help?')}
                  onClick={() => navigate(paths.dashboard.support.root.getHref())}
                >
                  <HelpCircle className='size-4' aria-hidden='true' />
                </SmartButton>
              </TooltipTrigger>
              <TooltipContent side='bottom'>
                <p>{t('dashboard:header.help.tooltip', 'Need help?')}</p>
              </TooltipContent>
            </Tooltip>

            {/* QR Code Modal */}
            <Tooltip>
              <MemberQRCodeDialog
                user={user}
                trigger={
                  <TooltipTrigger asChild>
                    <SmartButton
                      size='icon'
                      variant='ghost'
                      className='text-muted-foreground relative size-8 rounded-sm shadow-none'
                      aria-label={t('dashboard:header.qrCode.ariaLabel', 'Member QR Code')}
                    >
                      <QrCode className='size-4' aria-hidden='true' />
                    </SmartButton>
                  </TooltipTrigger>
                }
              />
              <TooltipContent side='bottom'>
                <p>{t('dashboard:header.qrCode.tooltip', 'Member QR Code')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <LanguageSwitcher />

          <UserDropdown />
        </div>
      </header>
    </>
  )
}
